import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import i18n from '../../i18n';
import { QUESTIONS } from './questions';

// Beta feedback form for the customers we invited to the new WiseClean app
// (/external/beta-feedback/:lang/:userId, link goes out by email).
//
// Three open questions, one page, no branching. Only the first is required;
// everyone who submits gets a €5 code either way.
//
// :userId is the Airtable Users rec-id carried in the emailed link.
// Copy lives in src/i18n.js (betaFeedback*), structure in ./questions.js.
const ENDPOINT = 'https://api.wiseclean.ee/api:nnuLSRT_/beta-feedback';

const STATE = {
    FORM: 'form',
    SUBMITTING: 'submitting',
    THANKS: 'thanks',
    ALREADY: 'already',
    ERROR: 'error',
};

const C = {
    cyan: '#6DCBDE',
    cyanText: '#1A7D8E',
    // Brand cyan at low alpha for the selected row fill. Same hue, no new colour.
    cyanTint: 'rgba(109, 203, 222, 0.14)',
    text: '#1A1A2E',
    // --wc-text-secondary from src/styles/brand.css, for help text.
    textSecondary: '#4A4A68',
    pageBg: '#F7F7FA',
    surface: '#FFFFFF',
    border: '#E5E5EC',
    error: '#EF4444',
    errorText: '#C62828',
};

const FONT = '"DM Sans", system-ui, -apple-system, "Segoe UI", Roboto, sans-serif';

// Focus-visible cannot be expressed as an inline style object, and this page
// must not touch the shared stylesheet, so it carries its own scoped rules.
const SCOPED_CSS = `
.bf-text:focus-visible, .bf-submit:focus-visible, .bf-retry:focus-visible {
  outline: 2px solid ${C.cyanText};
  outline-offset: 2px;
}
.bf-text::placeholder { color: #9A9AAE; }
`;

const BetaFeedback = () => {
    const { t } = useTranslation();
    const { lang, userId } = useParams();

    const [state, setState] = useState(STATE.FORM);
    const [answers, setAnswers] = useState({});
    const [errors, setErrors] = useState({});

    // Synchronous lock. `state` updates asynchronously, so a fast double-tap
    // could slip a second request past a state check. Released on failure only.
    const submittingRef = useRef(false);
    const questionRefs = useRef({});

    // Only en/et are supported, anything else falls back to en (same as CommReply).
    const normalizedLang = ['en', 'et'].includes(lang) ? lang : 'en';

    useEffect(() => {
        i18n.changeLanguage(normalizedLang);
    }, [normalizedLang]);

    useEffect(() => {
        document.title = t('betaFeedbackTitle');
    }, [t, normalizedLang]);

    // The result screens are short. Someone who submits from the bottom of a
    // long form would otherwise land on blank space.
    useEffect(() => {
        if (state === STATE.FORM || state === STATE.SUBMITTING) return;
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, [state]);

    const setAnswer = useCallback((id, value) => {
        setAnswers((prev) => ({ ...prev, [id]: value }));
        setErrors((prev) => {
            if (!prev[id]) return prev;
            const next = { ...prev };
            delete next[id];
            return next;
        });
    }, []);

    const validate = useCallback(() => {
        const found = {};
        QUESTIONS.forEach((question) => {
            if (!question.required) return;
            if (!String(answers[question.id] || '').trim()) {
                found[question.id] = t('betaFeedbackRequiredText');
            }
        });
        return found;
    }, [answers, t]);

    // Skipped optional questions are left out entirely, so "not answered" stays
    // distinguishable from "answered with nothing".
    const buildAnswers = useCallback(() => {
        const payload = {};
        QUESTIONS.forEach((question) => {
            const trimmed = String(answers[question.id] || '').trim();
            if (trimmed) payload[question.id] = trimmed;
        });
        return payload;
    }, [answers]);

    const handleSubmit = async (event) => {
        if (event) event.preventDefault();
        if (submittingRef.current) return;

        const found = validate();
        if (Object.keys(found).length > 0) {
            setErrors(found);
            const firstId = QUESTIONS.find((question) => found[question.id])?.id;
            const node = firstId ? questionRefs.current[firstId] : null;
            if (node) node.scrollIntoView({ behavior: 'smooth', block: 'center' });
            return;
        }

        submittingRef.current = true;
        setState(STATE.SUBMITTING);

        try {
            // Form-encoded keeps this a CORS "simple request" (no preflight),
            // same as CommReply. The answer map travels as a JSON string.
            const body = new URLSearchParams({
                user_recid: userId || '',
                lang: normalizedLang,
                answers: JSON.stringify(buildAnswers()),
                // `installed` is deliberately not sent any more: the form no
                // longer asks. It is optional on ws5 beta_feedback_POST, so the
                // Telegram digest just prints "installed: ?" instead of a
                // yes/no we would now be inventing.
                user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
            });

            const response = await fetch(ENDPOINT, {
                method: 'POST',
                headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                body: body.toString(),
            });

            if (!response.ok) throw new Error(`Unexpected status ${response.status}`);

            const data = await response.json().catch(() => null);
            if (data && data.ok === false) throw new Error('Endpoint reported ok:false');

            setState(data && data.already === true ? STATE.ALREADY : STATE.THANKS);
        } catch (error) {
            console.error('beta-feedback submit failed:', error);
            submittingRef.current = false;
            setState(STATE.ERROR);
        }
    };

    const renderText = (question) => (
        <textarea
            className="bf-text"
            id={`bf-field-${question.id}`}
            value={answers[question.id] || ''}
            onChange={(event) => setAnswer(question.id, event.target.value)}
            rows={4}
            aria-invalid={errors[question.id] ? true : undefined}
            aria-describedby={errors[question.id] ? `bf-error-${question.id}` : undefined}
            style={{ ...S.textarea, ...(errors[question.id] ? S.textareaError : null) }}
        />
    );

    const renderQuestion = (question) => {
        const error = errors[question.id];

        return (
            <div
                key={question.id}
                style={S.question}
                ref={(node) => {
                    questionRefs.current[question.id] = node;
                }}
            >
                <label
                    id={`bf-label-${question.id}`}
                    htmlFor={`bf-field-${question.id}`}
                    style={S.questionLabel}
                >
                    {t(`betaFeedbackQ_${question.id}`)}
                </label>
                {question.help && (
                    <p style={S.questionHelp}>{t(`betaFeedbackQ_${question.id}_help`)}</p>
                )}

                {renderText(question)}

                {error && (
                    <p id={`bf-error-${question.id}`} style={S.fieldError} role="alert">
                        {error}
                    </p>
                )}
            </div>
        );
    };

    const renderResult = (title, body, retry) => (
        <div style={S.card}>
            <img src="/wiseclean-logo.png" alt="WiseClean" style={S.logo} />
            {title && <h1 style={S.resultTitle}>{title}</h1>}
            <p style={S.resultBody}>{body}</p>
            {retry && (
                <button
                    type="button"
                    className="bf-retry"
                    onClick={() => setState(STATE.FORM)}
                    style={S.secondaryButton}
                >
                    {t('betaFeedbackTryAgain')}
                </button>
            )}
        </div>
    );

    let content;
    if (state === STATE.THANKS) {
        content = renderResult(t('betaFeedbackThanksTitle'), t('betaFeedbackThanksBody'), false);
    } else if (state === STATE.ALREADY) {
        content = renderResult(t('betaFeedbackThanksTitle'), t('betaFeedbackAlreadyBody'), false);
    } else if (state === STATE.ERROR) {
        content = renderResult(null, t('betaFeedbackErrorBody'), true);
    } else {
        const isSubmitting = state === STATE.SUBMITTING;

        content = (
            <form style={S.card} onSubmit={handleSubmit} noValidate>
                <img src="/wiseclean-logo.png" alt="WiseClean" style={S.logo} />
                <h1 style={S.title}>{t('betaFeedbackTitle')}</h1>
                <p style={S.intro}>{t('betaFeedbackIntro')}</p>

                {QUESTIONS.map(renderQuestion)}

                <button
                    type="submit"
                    className="bf-submit"
                    disabled={isSubmitting}
                    style={{ ...S.submit, ...(isSubmitting ? S.submitBusy : null) }}
                >
                    {isSubmitting ? t('betaFeedbackSubmitting') : t('betaFeedbackSubmit')}
                </button>
            </form>
        );
    }

    return (
        <div style={S.page}>
            <style>{SCOPED_CSS}</style>
            {content}
        </div>
    );
};

const S = {
    page: {
        minHeight: '100vh',
        backgroundColor: C.pageBg,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'flex-start',
        padding: '24px 16px 48px',
        fontFamily: FONT,
        color: C.text,
    },
    card: {
        width: '100%',
        maxWidth: '480px',
        boxSizing: 'border-box',
        backgroundColor: C.surface,
        borderRadius: '16px',
        boxShadow: '0 4px 24px rgba(26, 26, 46, 0.06)',
        padding: '28px 20px 24px',
    },
    logo: {
        height: '32px',
        objectFit: 'contain',
        display: 'block',
        marginBottom: '20px',
    },
    title: {
        margin: '0 0 12px',
        fontSize: '24px',
        lineHeight: 1.25,
        fontWeight: 700,
        color: C.text,
    },
    intro: {
        margin: '0 0 8px',
        fontSize: '16px',
        lineHeight: 1.55,
        color: C.textSecondary,
    },

    question: {
        paddingTop: '28px',
        marginTop: '20px',
        borderTop: `1px solid ${C.border}`,
    },
    questionLabel: {
        display: 'block',
        fontSize: '17px',
        lineHeight: 1.4,
        fontWeight: 600,
        color: C.text,
    },
    questionHelp: {
        margin: '6px 0 0',
        fontSize: '14px',
        lineHeight: 1.5,
        color: C.textSecondary,
    },




    textarea: {
        marginTop: '14px',
        width: '100%',
        boxSizing: 'border-box',
        padding: '12px 14px',
        borderWidth: '1.5px',
        borderStyle: 'solid',
        borderColor: C.border,
        borderRadius: '10px',
        backgroundColor: C.surface,
        color: C.text,
        fontFamily: FONT,
        // 16px keeps iOS Safari from zooming the page when the field is focused.
        fontSize: '16px',
        lineHeight: 1.5,
        resize: 'vertical',
    },
    textareaError: {
        borderColor: C.error,
    },
    fieldError: {
        margin: '8px 0 0',
        fontSize: '14px',
        lineHeight: 1.4,
        // Brand #EF4444 on white is 3.76:1, under AA for text this size. The
        // darker shade keeps the same hue and passes at 5.9:1. The border of
        // the offending field still uses the brand red, where contrast rules
        // for text do not apply.
        color: C.errorText,
    },

    submit: {
        marginTop: '28px',
        width: '100%',
        minHeight: '52px',
        padding: '14px 16px',
        border: 'none',
        borderRadius: '8px',
        backgroundColor: C.cyan,
        color: C.surface,
        fontFamily: FONT,
        fontSize: '16px',
        fontWeight: 600,
        cursor: 'pointer',
    },
    submitBusy: {
        opacity: 0.65,
        cursor: 'default',
    },
    secondaryButton: {
        marginTop: '20px',
        width: '100%',
        minHeight: '48px',
        padding: '12px 16px',
        border: `1.5px solid ${C.cyan}`,
        borderRadius: '8px',
        backgroundColor: C.surface,
        color: C.cyanText,
        fontFamily: FONT,
        fontSize: '16px',
        fontWeight: 600,
        cursor: 'pointer',
    },

    resultTitle: {
        margin: '0 0 12px',
        fontSize: '24px',
        lineHeight: 1.25,
        fontWeight: 700,
        color: C.text,
    },
    resultBody: {
        margin: 0,
        fontSize: '16px',
        lineHeight: 1.55,
        color: C.textSecondary,
    },
};

export default BetaFeedback;
